/* eslint-disable complexity */
import { Box, Typography, List } from '@mui/material';
import { useTranslation } from 'next-i18next';
import type { FC } from 'react';

import Button from 'components/Button';

import type { DeviceConfigSections } from '..';
import { ConfigurationListItem } from './ConfigurationListItem';

interface GroupConfigurationProps {
  countChargers: number;
  countControllers: number;
  ocppUrl: string | null;
  selectedCountry: string | null;
  technicianPassword: string | null;
  loadBalancing: string | null;
  grid: string | null;
  devicePassword: string | null;
  digitalInput: string | null;
  ssid: string | null;
  isDigitalInputSupported: boolean;
  isLoadingCountry: boolean;
  isLoadingGrid: boolean;
  isLoadingDigitalInput: boolean;
  isLoadingTechnicianPassword: boolean;
  isLoadingOcpp: boolean;
  isLoadingLoadBalancing: boolean;
  isLoadingDevicePassword: boolean;
  isLoadingNetwork: boolean;
  isSingleDeviceConfig: boolean;
  handleGroupConfiguration: () => void;
  handleOnClickOpen: (openScreen: DeviceConfigSections) => void;
}

const GroupConfiguration: FC<GroupConfigurationProps> = ({
  countChargers,
  countControllers,
  ocppUrl,
  selectedCountry,
  technicianPassword,
  loadBalancing,
  grid,
  devicePassword,
  digitalInput,
  ssid,
  isDigitalInputSupported,
  isLoadingCountry,
  isLoadingGrid,
  isLoadingDigitalInput,
  isLoadingTechnicianPassword,
  isLoadingOcpp,
  isLoadingLoadBalancing,
  isLoadingDevicePassword,
  isLoadingNetwork,
  isSingleDeviceConfig,
  handleOnClickOpen,
  handleGroupConfiguration,
}) => {
  const { t } = useTranslation();

  const hasChargers = countChargers > 0;
  const hasControllers = countControllers > 0;
  const hasBothDeviceTypes = hasChargers && hasControllers;
  const hasOnlyChargers = hasChargers && !hasControllers;
  const hasOnlyControllers = hasControllers && !hasChargers;

  const isButtonDisabled =
    isLoadingCountry ||
    isLoadingGrid ||
    isLoadingDigitalInput ||
    isLoadingTechnicianPassword ||
    isLoadingOcpp ||
    isLoadingLoadBalancing ||
    isLoadingDevicePassword ||
    isLoadingNetwork ||
    (!loadBalancing &&
      !ocppUrl &&
      !selectedCountry &&
      !grid &&
      !technicianPassword &&
      !digitalInput &&
      !devicePassword &&
      !ssid);

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h2" textAlign="center" mb={2}>
          {t(
            'common:pages.devices.device_list.group_config.group_config_title',
          )}
        </Typography>
        <Typography variant="body1" textAlign="center">
          {t(
            isSingleDeviceConfig
              ? 'common:pages.devices.device_list.group_config.single_config_desc'
              : 'common:pages.devices.device_list.group_config.group_config_desc',
          )}
        </Typography>
      </Box>
      <List sx={{ overflow: 'auto', height: '50vh' }}>
        {hasBothDeviceTypes || hasControllers ? (
          <Box>
            {hasBothDeviceTypes ? (
              <Box mt={2} p={1}>
                <Typography variant="body1" fontWeight={600}>
                  {t('common:pages.devices.device_list.group_config.devices')}
                </Typography>
              </Box>
            ) : null}
            <ConfigurationListItem
              isFirstItem
              onClick={() => handleOnClickOpen('devicePassword')}
              title={t(
                'common:pages.devices.device_list.group_config.sections.device_password',
              )}
              value={devicePassword}
              isLoading={isLoadingDevicePassword}
            />
            <ConfigurationListItem
              onClick={() => handleOnClickOpen('wifiNetwork')}
              title={t(
                'common:pages.devices.device_list.group_config.sections.network',
              )}
              value={ssid}
              isLoading={isLoadingNetwork}
              isLastItem={hasBothDeviceTypes || hasOnlyControllers}
            />
          </Box>
        ) : null}
        {hasBothDeviceTypes || hasChargers ? (
          <Box>
            {hasBothDeviceTypes ? (
              <Box mt={2} p={1}>
                <Typography variant="body1" fontWeight={600}>
                  {t(
                    'common:pages.devices.device_list.group_config.chargers_only',
                  )}
                </Typography>
              </Box>
            ) : null}
            <ConfigurationListItem
              onClick={() => handleOnClickOpen('country')}
              title={t(
                'common:pages.devices.device_list.group_config.sections.country',
              )}
              value={selectedCountry}
              isLoading={isLoadingCountry}
              isFirstItem={hasBothDeviceTypes || hasOnlyChargers}
            />
            <ConfigurationListItem
              onClick={() => handleOnClickOpen('grid')}
              title={t(
                'common:pages.devices.device_list.group_config.sections.grid',
              )}
              value={grid}
              isLoading={isLoadingGrid}
            />
            {isDigitalInputSupported ? (
              <ConfigurationListItem
                onClick={() => handleOnClickOpen('digitalInput')}
                title={t(
                  'common:pages.devices.device_list.group_config.sections.digital_input',
                )}
                value={digitalInput}
                isLoading={isLoadingDigitalInput}
              />
            ) : null}
            <ConfigurationListItem
              onClick={() => handleOnClickOpen('loadBalancing')}
              title={t(
                'common:pages.devices.device_list.group_config.sections.load_balancing',
              )}
              value={loadBalancing}
              isLoading={isLoadingLoadBalancing}
            />
            <ConfigurationListItem
              onClick={() => handleOnClickOpen('technicianPassword')}
              title={t(
                'common:pages.devices.device_list.group_config.sections.tech_password',
              )}
              value={technicianPassword}
              isLoading={isLoadingTechnicianPassword}
            />
            {hasOnlyChargers ? (
              <>
                <ConfigurationListItem
                  onClick={() => handleOnClickOpen('devicePassword')}
                  title={t(
                    'common:pages.devices.device_list.group_config.sections.device_password',
                  )}
                  value={devicePassword}
                  isLoading={isLoadingDevicePassword}
                />
                <ConfigurationListItem
                  onClick={() => handleOnClickOpen('wifiNetwork')}
                  title={t(
                    'common:pages.devices.device_list.group_config.sections.network',
                  )}
                  value={ssid ? ssid : null}
                  isLoading={isLoadingNetwork}
                />
              </>
            ) : null}
            <ConfigurationListItem
              onClick={() => handleOnClickOpen('ocpp')}
              title={t(
                'common:pages.devices.device_list.group_config.sections.ocpp',
              )}
              value={ocppUrl}
              isLoading={isLoadingOcpp}
              isLastItem
            />
          </Box>
        ) : null}
      </List>
      <Box mt={3}>
        <Button
          fullWidth
          variant="flat"
          size="large"
          disabled={isButtonDisabled}
          onClick={handleGroupConfiguration}
        >
          {t('common:apply')}
        </Button>
      </Box>
    </Box>
  );
};

export default GroupConfiguration;
